import { PROPOSTA_SITUACAO } from "@repo/core/services/entities/Proposals";
import type {
  ProposalPhoneTokenStatusResponseDTO,
  UpdateProposalPhoneTokenStatusParamsDTO
} from "@repo/core/services/entities/WayupRequisitions";
import type { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import type { PreProposalData } from "../store/preProposalStore";

const onlyDigits = (value: string | number | null | undefined): string =>
  String(value ?? "").replace(/\D/g, "");

export const isDraftProposalForPhoneTokenStatus = (
  proposal?: Pick<PreProposalData, "strSituacao"> | null
): boolean =>
  proposal?.strSituacao === PROPOSTA_SITUACAO.DOCUMENT_CONFERENCE ||
  proposal?.strSituacao === PROPOSTA_SITUACAO.DATA_CONFERENCE;

export const hasReusableProposalPhoneTokenValidation = (
  proposal:
    | Pick<PreProposalData, "strEndDdd1" | "strEndFone1">
    | null
    | undefined,
  status: ProposalPhoneTokenStatusResponseDTO | null | undefined
): boolean => {
  if (status?.tokenvalidado?.trim().toUpperCase() !== "S") {
    return false;
  }

  const currentPhone = `${onlyDigits(proposal?.strEndDdd1)}${onlyDigits(
    proposal?.strEndFone1
  )}`;
  const validatedPhone = `${onlyDigits(status.dddenviotoken)}${onlyDigits(
    status.foneenviotoken
  )}`;

  return !!currentPhone && currentPhone === validatedPhone;
};

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildProposalPhoneTokenValidationParams = (
  proposal: Pick<PreProposalData, "lngProposta" | "strEndDdd1" | "strEndFone1">,
  date = new Date()
): UpdateProposalPhoneTokenStatusParamsDTO | undefined => {
  const proposalId = proposal.lngProposta;
  const phoneDDD = onlyDigits(proposal.strEndDdd1);
  const phone = onlyDigits(proposal.strEndFone1);

  if (!proposalId || !phoneDDD || !phone) {
    return undefined;
  }

  return {
    varproposta: String(proposalId),
    vardatatoken: formatLocalDate(date),
    vardddenviotoken: phoneDDD,
    varfoneenviotoken: phone,
    vartokenvalidado: "S"
  };
};

type ProposalPhoneTokenStatusResource = Pick<
  RequisitionsResource,
  "updateProposalPhoneTokenStatus"
>;

type ErrorLogger = (message: string, error: unknown) => void;

export const registerValidatedProposalPhoneToken = async (
  resource: ProposalPhoneTokenStatusResource,
  proposal: Pick<PreProposalData, "lngProposta" | "strEndDdd1" | "strEndFone1">,
  date = new Date(),
  logError: ErrorLogger = console.error
): Promise<void> => {
  const params = buildProposalPhoneTokenValidationParams(proposal, date);
  if (!params) {
    return;
  }

  try {
    await resource.updateProposalPhoneTokenStatus(params);
  } catch (error) {
    logError("Error saving proposal phone token status:", error);
  }
};

export const continueAfterValidatedProposalPhoneToken = async (
  resource: ProposalPhoneTokenStatusResource,
  proposal: Pick<PreProposalData, "lngProposta" | "strEndDdd1" | "strEndFone1">,
  continueFlow: () => Promise<void>,
  date = new Date(),
  logError: ErrorLogger = console.error
): Promise<void> => {
  await registerValidatedProposalPhoneToken(resource, proposal, date, logError);
  await continueFlow();
};
